import {
  AlertCircle,
  ArrowLeft,
  FileText,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import {
  useGetCoveragesQuery,
  useGetMentionedCoveragesQuery,
} from '@/services/api/coverageService';

import { MentionDetails } from './mention-details/MentionDetails';
import { MentionsList } from './mentions-list/MentionsList';
import { MentionsOverview } from './mentions-overview/MentionsOverview';

interface CoverageMentionsDebugPageProps {
  matterId: string;
}

export const CoverageMentionsDebugPage = ({
  matterId,
}: CoverageMentionsDebugPageProps) => {
  const navigate = useNavigate();
  const [selectedMentionId, setSelectedMentionId] = useState<string | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState<'overview' | 'list' | 'details'>(
    'overview',
  );

  const {
    data: mentions,
    isLoading,
    error,
    refetch,
    isFetching,
  } = useGetMentionedCoveragesQuery({ matterId });

  const { data: coverages, isLoading: isLoadingCoverages } =
    useGetCoveragesQuery({ matterId });

  const selectedMention = mentions?.find((m) => m.id === selectedMentionId);

  const handleMentionSelect = (mentionId: string) => {
    setSelectedMentionId(mentionId);
    setActiveTab('details');
  };

  const handleBackToList = () => {
    setSelectedMentionId(null);
    setActiveTab('list');
  };

  const handleBackToCoverage = () => {
    navigate(`/matters/${matterId}/coverage`);
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-2 text-gray-600">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading coverage mentions...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600 mb-4">
              <AlertCircle className="h-5 w-5" />
              <span>Error loading coverage mentions</span>
            </div>
            <Button
              variant="outline"
              onClick={() => refetch()}
              disabled={isFetching}
              className="w-full"
            >
              {isFetching ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!mentions || mentions.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <Card className="w-96">
          <CardContent className="pt-6 text-center">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Coverage Mentions Found
            </h3>
            <p className="text-gray-500 mb-4">
              No coverage mentions have been extracted for this matter yet.
            </p>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 h-full overflow-hidden flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBackToCoverage}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Coverage
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                Coverage Mentions Debug
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Debug view for all coverage mentions extracted from artifacts (
                {mentions.length} total)
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            className="gap-2"
          >
            {isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Refresh
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <Tabs
          value={activeTab}
          onValueChange={(tab) => setActiveTab(tab as any)}
          className="h-full flex flex-col"
        >
          {/* Modern Tab Navigation */}
          <div className="bg-white px-6 pt-4 pb-2">
            <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg max-w-fit">
              <button
                type="button"
                onClick={() => setActiveTab('overview')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  activeTab === 'overview'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Overview
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('list')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  activeTab === 'list'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Mentions List
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('details')}
                disabled={!selectedMention}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  activeTab === 'details' && selectedMention
                    ? 'bg-white text-gray-900 shadow-sm'
                    : selectedMention
                      ? 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      : 'text-gray-400 cursor-not-allowed'
                }`}
              >
                Details
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <TabsContent value="overview" className="mt-0">
              <MentionsOverview
                mentions={mentions}
                onMentionSelect={handleMentionSelect}
              />
            </TabsContent>

            <TabsContent value="list" className="mt-0">
              <MentionsList
                mentions={mentions}
                onMentionSelect={handleMentionSelect}
                availableCoverages={coverages || []}
              />
            </TabsContent>

            <TabsContent value="details" className="mt-0">
              {selectedMention ? (
                <MentionDetails
                  mention={selectedMention}
                  onBack={handleBackToList}
                />
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Select a mention to view details
                </div>
              )}
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
};
